import { Fragment, useMemo, useState } from 'react';
import type { FsnbWorkCoefficient } from '@shared/api';
import { fmtNum } from '@shared/lib/smetaLabels';
import {
  COEF_PARTS,
  coefResourceText,
  coefScopeLabel,
  coefValueText,
  fullCode,
} from '../../normBaseLabels';
import { COLLAPSE_COLLECTION_OVER, groupCoefficients } from '../helpers';
import { CoefNote, Grid, GroupToggle, TableWrap } from '../styles';

/** Поправочные коэффициенты нормы: значения к составляющим расхода и свои
 *  значения для её ресурсов. */
export function CoefficientsTable({
  items,
  baseType,
  onOpenCoefficient,
}: {
  items: FsnbWorkCoefficient[];
  baseType: string;
  onOpenCoefficient?: (code: string) => void;
}) {
  const groups = useMemo(() => groupCoefficients(items), [items]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const cols = 2 + COEF_PARTS.length;
  return (
    <TableWrap>
      <Grid>
        <thead>
          <tr>
            <th>Код</th>
            <th>Условие применения</th>
            {COEF_PARTS.map((p) => (
              <th key={p.key} className="num" title={p.title}>
                {p.short}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const collapsible =
              g.scope === 'collection' && g.items.length > COLLAPSE_COLLECTION_OVER;
            const open = !collapsible || expanded.has(g.key);
            return (
              <Fragment key={g.key}>
                <tr className="group">
                  <td colSpan={cols}>
                    {g.scope === 'work'
                      ? 'К этой норме'
                      : `${coefScopeLabel(g.scope).replace(/^к /, 'К ')} ${fullCode(baseType, g.code)}`}
                    {` · ${fmtNum(g.items.length)}`}
                    {collapsible && (
                      <GroupToggle
                        type="button"
                        aria-expanded={open}
                        onClick={() =>
                          setExpanded((s) => {
                            const next = new Set(s);
                            if (next.has(g.key)) next.delete(g.key);
                            else next.add(g.key);
                            return next;
                          })
                        }
                      >
                        {open ? 'Свернуть' : 'Показать'}
                      </GroupToggle>
                    )}
                  </td>
                </tr>
                {open &&
                  g.items.map((c) => (
                    <tr
                      key={c.code}
                      className={onOpenCoefficient ? 'clickable' : undefined}
                      onClick={onOpenCoefficient ? () => onOpenCoefficient(c.code) : undefined}
                    >
                      <td className="code small">{c.code}</td>
                      <td>
                        {c.name}
                        {c.resources.length > 0 && (
                          <CoefNote>
                            Для ресурсов нормы:{' '}
                            {c.resources
                              .map((r) => `${r.resource_code} — ${coefResourceText(r)}`)
                              .join('; ')}
                          </CoefNote>
                        )}
                      </td>
                      {COEF_PARTS.map((p) => (
                        <td key={p.key} className="num">
                          {coefValueText(c, p.key)}
                        </td>
                      ))}
                    </tr>
                  ))}
              </Fragment>
            );
          })}
        </tbody>
      </Grid>
    </TableWrap>
  );
}
